import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { customersApi } from '@/api/modules/customers';
import { paths } from '@/router/paths';

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useQuery({
    queryKey: ['customer', id],
    queryFn: () => customersApi.get(id!),
    enabled: Boolean(id)
  });

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return (
      <Box>
        <BackToList />
        <Alert severity="error">
          Failed to load customer:{' '}
          {query.error instanceof Error ? query.error.message : 'Not found'}
        </Alert>
      </Box>
    );
  }

  const c = query.data;

  return (
    <Box>
      <BackToList />

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h1" gutterBottom>
            {c.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {c.code} · Added {formatDateTime(c.createdAt)}
          </Typography>
        </Box>
        <Chip
          label={c.active ? 'ACTIVE' : 'INACTIVE'}
          color={c.active ? 'success' : 'default'}
          sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
        />
      </Stack>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={1.5} divider={<Divider flexItem />}>
            <DetailRow label="Rate per Wh">
              <Typography variant="h3">
                {c.currency} {c.perWhRate} <Typography component="span" variant="body2" color="text.secondary">/ Wh</Typography>
              </Typography>
            </DetailRow>
            <DetailRow label="Tax ID / GSTIN">
              <Typography>{c.taxId || <MutedText>Not set</MutedText>}</Typography>
            </DetailRow>
            <DetailRow label="Email">
              <Typography>{c.email || <MutedText>Not set</MutedText>}</Typography>
            </DetailRow>
            <DetailRow label="Phone">
              <Typography>{c.phone || <MutedText>Not set</MutedText>}</Typography>
            </DetailRow>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

function BackToList() {
  const navigate = useNavigate();
  return (
    <Button
      startIcon={<ArrowBackIcon />}
      size="small"
      onClick={() => navigate(paths.customers)}
      sx={{ mb: 2 }}
    >
      Back to customers
    </Button>
  );
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
        {label}
      </Typography>
      <Box>{children}</Box>
    </Stack>
  );
}

function MutedText({ children }: { children: React.ReactNode }) {
  return (
    <Typography component="span" variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
      {children}
    </Typography>
  );
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return iso; }
}
